import { tripletToHex } from "../../utils/directionColors";

// Map our internal interval values (localStorage key PERPS_CHART_INTERVAL, e.g.
// "15m") to TradingView's resolution codes ("15", "60", "D", …).
export const TV_INTERVAL: Record<string, string> = {
  "1m": "1",
  "5m": "5",
  "15m": "15",
  "1h": "60",
  "4h": "240",
  "1d": "D",
};

export function tvIntervalFromLS(): string {
  const ls =
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("PERPS_CHART_INTERVAL")) ||
    "15m";
  return TV_INTERVAL[ls] || "15";
}

function readTriplet(name: string, fallback: string): string {
  const raw =
    typeof window !== "undefined"
      ? getComputedStyle(document.documentElement).getPropertyValue(name).trim()
      : "";
  return raw || fallback;
}

// The TradingView candle background/grid/axis palette. Kept in sync with the
// self-hosted LightweightCandles fallback so the two chart paths look identical.
const CHART_BG = "#0e0e14";
const CHART_GRID = "rgba(255, 255, 255, 0.05)";
const CHART_AXIS_TEXT = "#9598a1";

/**
 * Build TradingView `overrides` from the LIVE theme (the user-selected
 * --color-up / --color-down direction colors) so the embedded widget's candles,
 * background and grid match the rest of the app and follow the color-scheme
 * setting. TradingView override values want HEX, so we convert the CSS triplet
 * via tripletToHex (the same helper the fallback candle chart uses).
 */
export function buildOverrides(): Record<string, string | number | boolean> {
  const up = tripletToHex(readTriplet("--color-up", "22 199 132"));
  const down = tripletToHex(readTriplet("--color-down", "234 57 67"));
  return {
    "paneProperties.background": CHART_BG,
    "paneProperties.backgroundType": "solid",
    "paneProperties.vertGridProperties.color": CHART_GRID,
    "paneProperties.horzGridProperties.color": CHART_GRID,
    "scalesProperties.textColor": CHART_AXIS_TEXT,
    "mainSeriesProperties.candleStyle.upColor": up,
    "mainSeriesProperties.candleStyle.downColor": down,
    "mainSeriesProperties.candleStyle.borderUpColor": up,
    "mainSeriesProperties.candleStyle.borderDownColor": down,
    "mainSeriesProperties.candleStyle.wickUpColor": up,
    "mainSeriesProperties.candleStyle.wickDownColor": down,
    "mainSeriesProperties.candleStyle.drawWick": true,
    "mainSeriesProperties.candleStyle.drawBorder": true,
  };
}

/**
 * TradingView `studies_overrides` for the built-in Volume study so the volume
 * histogram's up/down bars follow the SAME theme colors as the candles (otherwise
 * they stay TradingView's default green/red and clash with e.g. the colorblind
 * blue/orange scheme). color.1 = up/rising bar, color.0 = down/falling bar.
 */
export function buildStudiesOverrides(): Record<string, string | number> {
  const up = tripletToHex(readTriplet("--color-up", "22 199 132"));
  const down = tripletToHex(readTriplet("--color-down", "234 57 67"));
  return {
    "volume.volume.color.0": down,
    "volume.volume.color.1": up,
    "volume.volume.transparency": 60,
  };
}

/** Live theme colors as hex, for the chart's position/order lines. */
export function themeLineColors() {
  return {
    up: tripletToHex(readTriplet("--color-up", "22 199 132")),
    down: tripletToHex(readTriplet("--color-down", "234 57 67")),
    // DESIGN.md `warning` — liquidation risk.
    warning: "#FF7A00",
    text: "#FFFFFF",
    bg: CHART_BG,
  };
}

export { CHART_BG };
